/**
 * Dashboard — portfolio overview page.
 *
 * Stat cards row + Risk Matrix chart + sortable project table.
 * "New Assessment" button opens a dialog to create a project.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, LayoutGrid, ArrowUpDown, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { RiskMatrix } from '@/components/charts/RiskMatrix';
import { useProjectsWithScores, useSaveProject } from '@/hooks/useRCAData';
import { useRCA } from '@/context/RCAContext';
import { DEPARTMENTS } from '@/data/rcaQuestions';
import { cn } from '@/lib/utils';
import type { RCACategory, RCAProject, RiskLevel } from '@/types/rca';

// ============================================================
// Constants
// ============================================================

type SortKey = 'name' | 'status' | 'department' | 'updated_at';

// ============================================================
// Helpers
// ============================================================

function levelBadgeClass(level: RiskLevel | string): string {
  switch (level) {
    case 'High':
      return 'bg-risk-high/10 text-risk-high border-risk-high/30';
    case 'Medium':
      return 'bg-risk-medium/10 text-risk-medium border-risk-medium/30';
    case 'Low':
      return 'bg-risk-low/10 text-risk-low border-risk-low/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function categoryBadgeClass(cat: RCACategory | number | null | undefined): string {
  switch (cat) {
    case 4:
      return 'bg-cat-4/15 text-cat-4 border-cat-4/40';
    case 3:
      return 'bg-cat-3/15 text-cat-3 border-cat-3/40';
    case 2:
      return 'bg-cat-2/15 text-cat-2 border-cat-2/40';
    case 1:
      return 'bg-cat-1/15 text-cat-1 border-cat-1/40';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

// ============================================================
// Stat Card
// ============================================================

function StatCard({
  label,
  value,
  sub,
  colorClass,
}: {
  label: string;
  value: string | number;
  sub?: string;
  colorClass?: string;
}) {
  return (
    <Card className="dashboard-card">
      <CardContent className="p-5 space-y-1">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className={cn('text-3xl font-bold tabular-nums', colorClass ?? 'text-foreground')}>
          {value}
        </p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ============================================================
// New Assessment Dialog
// ============================================================

interface NewAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (project: RCAProject) => void;
}

function NewAssessmentDialog({ open, onClose, onCreated }: NewAssessmentDialogProps) {
  const { dispatch } = useRCA();
  const saveProject = useSaveProject();

  const [projectName, setProjectName] = useState('');
  const [department, setDepartment] = useState('');
  const [assessor, setAssessor] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    if (!projectName.trim()) {
      setError('Project name is required.');
      return;
    }
    if (!department) {
      setError('Department is required.');
      return;
    }

    setError(null);
    try {
      const project = await saveProject.mutateAsync({
        name: projectName.trim(),
        department,
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      // Seed context with project metadata
      dispatch({
        type: 'SET_PROJECT_META',
        payload: {
          project: project.name,
          department: project.department,
          assessor: assessor.trim(),
        },
      });
      dispatch({ type: 'RESET' });
      dispatch({
        type: 'SET_PROJECT_META',
        payload: {
          project: project.name,
          department: project.department,
          assessor: assessor.trim(),
        },
      });

      onCreated(project);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create project.';
      setError(msg);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <p className="text-base font-semibold">New Assessment</p>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Project Name</label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g., ERP System Modernization"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Department</label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger>
                <SelectValue placeholder="Select department..." />
              </SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d.code} value={d.name}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Assessor Name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <Input
              value={assessor}
              onChange={(e) => setAssessor(e.target.value)}
              placeholder="Full name"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => void handleCreate()}
              disabled={saveProject.isPending}
              className="bg-cot-blue hover:bg-cot-blue/90 text-white"
            >
              {saveProject.isPending ? 'Creating...' : 'Create & Start'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Main Dashboard
// ============================================================

export default function Dashboard() {
  const navigate = useNavigate();
  const projectsQuery = useProjectsWithScores();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('updated_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [filterText, setFilterText] = useState('');

  const projects = projectsQuery.data ?? [];

  // Build matrix points — projects without scores get skipped from the chart
  const matrixPoints = projects
    .filter((p) => p.avg_risk != null)
    .map((p) => ({
      id: p.id,
      name: p.name,
      avgRiskScore: p.avg_risk ?? 250,
      avgComplexityScore: p.avg_complexity ?? 250,
      category: (p.category ?? 1) as RCACategory,
      budget: p.budget,
    }));

  // Stat computations
  const totalProjects = projects.length;
  const activeProjects = projects.filter((p) => p.status === 'active').length;
  const highRiskProjects = projects.filter((p) => p.risk_level === 'High').length;
  const riskScores = projects.map((p) => p.avg_risk).filter((s): s is number => s != null);
  const avgRisk =
    riskScores.length > 0
      ? Math.round(riskScores.reduce((a, b) => a + b, 0) / riskScores.length)
      : null;
  const complexityScores = projects
    .map((p) => p.avg_complexity)
    .filter((s): s is number => s != null);
  const avgComplexity =
    complexityScores.length > 0
      ? Math.round(complexityScores.reduce((a, b) => a + b, 0) / complexityScores.length)
      : null;

  // Sort + filter
  const filteredSorted = [...projects]
    .filter((p) =>
      filterText
        ? p.name.toLowerCase().includes(filterText.toLowerCase()) ||
          p.department.toLowerCase().includes(filterText.toLowerCase())
        : true,
    )
    .sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return sortAsc ? cmp : -cmp;
    });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  }

  function handleProjectCreated(project: RCAProject) {
    setDialogOpen(false);
    navigate('/assess');
  }

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LayoutGrid className="w-6 h-6 text-cot-blue" />
            <h1 className="text-2xl font-bold text-foreground">Portfolio Dashboard</h1>
          </div>
          <Button
            onClick={() => setDialogOpen(true)}
            className="gap-1.5 bg-cot-blue hover:bg-cot-blue/90 text-white"
          >
            <Plus className="w-4 h-4" />
            New Assessment
          </Button>
        </div>

        {/* Stat cards */}
        {projectsQuery.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Card key={i} className="dashboard-card">
                <CardContent className="p-5 space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Total Projects" value={totalProjects} />
            <StatCard
              label="Active"
              value={activeProjects}
              colorClass="text-cot-blue"
            />
            <StatCard
              label="High Risk"
              value={highRiskProjects}
              colorClass={highRiskProjects > 0 ? 'text-risk-high' : 'text-foreground'}
            />
            <StatCard
              label="Avg Risk Score"
              value={avgRisk ?? '—'}
              sub="100-500 scale"
            />
            <StatCard
              label="Avg Complexity"
              value={avgComplexity ?? '—'}
              sub="100-500 scale"
            />
          </div>
        )}

        {/* Main two-column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Risk Matrix */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">Risk vs. Complexity Matrix</h2>
            {projectsQuery.isLoading ? (
              <Skeleton className="h-[400px] w-full rounded-lg" />
            ) : matrixPoints.length === 0 ? (
              <div className="h-[400px] flex items-center justify-center rounded-lg border border-border bg-muted/20">
                <div className="text-center space-y-2 text-muted-foreground">
                  <AlertTriangle className="w-8 h-8 mx-auto opacity-40" />
                  <p className="text-sm">No scored assessments yet.</p>
                </div>
              </div>
            ) : (
              <Card className="dashboard-card">
                <CardContent className="p-4">
                  <RiskMatrix
                    projects={matrixPoints}
                    onProjectClick={(id) => navigate(`/project/${id}`)}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Project table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-foreground">Projects</h2>
              <Input
                placeholder="Filter..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="h-8 w-40 text-sm"
              />
            </div>

            {projectsQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : projectsQuery.isError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                Failed to load projects. Please refresh the page.
              </div>
            ) : filteredSorted.length === 0 ? (
              <div className="rounded-lg border border-border bg-muted/20 px-6 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {filterText ? 'No projects match your filter.' : 'No projects yet. Create your first assessment.'}
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      {(
                        [
                          { key: 'name', label: 'Name' },
                          { key: 'department', label: 'Dept' },
                          { key: 'status', label: 'Status' },
                        ] as { key: SortKey; label: string }[]
                      ).map((col) => (
                        <TableCell
                          key={col.key}
                          className="font-semibold text-muted-foreground text-xs uppercase cursor-pointer select-none"
                          onClick={() => toggleSort(col.key)}
                        >
                          <span className="flex items-center gap-1">
                            {col.label}
                            <ArrowUpDown className="w-3 h-3 opacity-50" />
                          </span>
                        </TableCell>
                      ))}
                      <TableCell className="font-semibold text-muted-foreground text-xs uppercase">
                        Risk
                      </TableCell>
                      <TableCell className="font-semibold text-muted-foreground text-xs uppercase">
                        Cat
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSorted.map((project) => (
                      <TableRow
                        key={project.id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => navigate(`/project/${project.id}`)}
                      >
                        <TableCell className="font-medium text-sm text-foreground max-w-[160px] truncate">
                          {project.name}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">
                          {project.department}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-xs capitalize',
                              project.status === 'active'
                                ? 'border-cot-blue/30 text-cot-blue bg-cot-blue/10'
                                : 'text-muted-foreground',
                            )}
                          >
                            {project.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {project.risk_level ? (
                            <Badge
                              variant="outline"
                              className={cn('text-xs', levelBadgeClass(project.risk_level))}
                            >
                              {project.risk_level}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {project.category ? (
                            <Badge
                              variant="outline"
                              className={cn('text-xs font-bold', categoryBadgeClass(project.category))}
                            >
                              {project.category}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Assessment Dialog */}
      <NewAssessmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleProjectCreated}
      />
    </div>
  );
}
